import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useState } from 'react';
import SFDynamicForm from '../sf-dynamic-form/Index.js';
import { WMSFetch } from '../../utils/common-api-calls.js';

export default function RFQSubmissionModal({
    open,
    setOpen,
    currentUser,
    setShowNotification,
    partId,
    partName,
    getRelatedPricingData,
}) {
    const [headerFormData, setHeaderFormData] = useState([]);
    const [productFormData, setProductFormData] = useState([]);
    const [lineFormData, setLineFormData] = useState([]);
    const [loadingFormData, setLoadingFormData] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const [currentVendorId, setCurrentVendorId] = useState('');

    const formData = [...lineFormData, ...productFormData, ...headerFormData];
    const isNotFilledOut = formData.some((field) => field.required && !field.value);

    const closeModal = () => {
        resetContactDropdownData();
        setOpen(false);
    };

    const resetContactDropdownData = () => {
        setHeaderFormData((prevData) => {
            return prevData.map((data) => {
                if (data.label === 'Contact') {
                    return {
                        ...data,
                        dropdownData: [],
                    };
                }
                return data;
            });
        });
    };

    const updateRelatedContactEntries = async (vendorId) => {
        try {
            resetContactDropdownData();
            const body = {
                vendorId,
            };
            const responseData = await WMSFetch('wms/sms/get-related-contacts', body, currentUser);
            if (!responseData || responseData?.error) throw Error();

            setHeaderFormData((prevData) => {
                return prevData.map((data) => {
                    if (data.label === 'Contact') {
                        return {
                            ...data,
                            picklistValues: responseData,
                        };
                    }
                    return data;
                });
            });
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error updating related contact entries.',
                message:
                    'An error has occured updating related contact entries for the selected vendor. If the error persists please contact your manager.',
            });
        }
    };

    const getRFQFields = async () => {
        try {
            setLoadingFormData(true);
            const rfqFieldData = await WMSFetch('wms/sms/get-rfq-fields', {}, currentUser);
            if (!rfqFieldData || rfqFieldData?.error) throw Error();

            setHeaderFormData(rfqFieldData.headerFields);
            setLineFormData(rfqFieldData.lineFields);
            setProductFormData(rfqFieldData.partField);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error retrieving data for RFQ fields',
                message:
                    'An error has occured retrieving data for RFQ fields. If the error persists please contact your manager.',
            });
        } finally {
            setLoadingFormData(false);
        }
    };

    const submitRFQ = async () => {
        try {
            setSubmitting(true);

            const body = {
                headerData: headerFormData,
                lineData: [...productFormData, ...lineFormData],
            };
            const rfqFieldData = await WMSFetch('wms/sms/submit-rfq', body, currentUser);
            if (rfqFieldData?.error?.message) {
                setShowNotification({
                    shown: true,
                    status: 'error',
                    title: 'Error During Submission',
                    message: rfqFieldData?.error?.message,
                });
                return;
            }
            if (!rfqFieldData || rfqFieldData?.error) throw Error();

            // Update pricing section below to display newly created RFQ
            setTimeout(async () => {
                await getRelatedPricingData();
            }, 5_000);

            setShowNotification({
                shown: true,
                status: 'success',
                title: 'Successfully Created RFQ',
                message: 'The RFQ has been successfully created and added to the Vendor Quote History table.',
            });
            closeModal();
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error submitting RFQ data',
                message: 'An error has occured submitting RFQ data. If the error persists please contact your manager.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        // Effect to get Contact entries when Vendor changes
        // The list of contacts must be related to the selected Vendor
        if (!open) return;
        const vendor = formData.find((data) => data.label === 'Vendor');
        if (vendor?.value !== '' && vendor?.value !== currentVendorId) {
            updateRelatedContactEntries(vendor?.value);
            setCurrentVendorId(vendor?.value);
        }
    }, [headerFormData]);

    useEffect(() => {
        getRFQFields();
    }, []);

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as='div' className='relative z-30' onClose={() => setOpen(false)}>
                <section className='fixed w-full h-full top-0 left-0 flex items-center justify-center'>
                    <Transition.Child
                        as={Fragment}
                        enter='ease-out duration-300'
                        enterFrom='opacity-0'
                        enterTo='opacity-100'
                        leave='ease-in duration-200'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <div className='fixed w-screen h-screen z-20 inset-0 bg-gray-500 bg-opacity-40 transition-opacity' />
                    </Transition.Child>
                    <section className='relative flex flex-col h-[600px] w-[650px] border rounded-md px-3 pb-5 pt-3 bg-white gap-y-3 z-30'>
                        <h1 className='w-full text-lg text-center'>RFQ Submission</h1>

                        {loadingFormData ? (
                            <div className='flex items-center justify-center w-full h-full'>
                                <div className='h-12 w-12 border-b-[2px] border-e-[2px] border-gray-500 rounded-full animate-spin' />
                            </div>
                        ) : (
                            <>
                                <section className='h-full overflow-y-scroll'>
                                    <section className='flex flex-col h-full'>
                                        <section>
                                            <h1 className='w-full bg-gray-100 text-left px-3 py-1'>Header Information</h1>
                                            <article className='grid grid-cols-2'>
                                                <SFDynamicForm
                                                    formData={headerFormData}
                                                    setFormData={setHeaderFormData}
                                                    cols={2}
                                                    notSerialized={true}
                                                />
                                            </article>
                                        </section>
                                        <section>
                                            <h1 className='w-full bg-gray-100 text-left px-3 py-1'>Line Information</h1>
                                            <article className='grid grid-cols-2'>
                                                <SFDynamicForm
                                                    formData={productFormData}
                                                    setFormData={setProductFormData}
                                                    cols={2}
                                                    notSerialized={true}
                                                    customQueryDefaults={[
                                                        { tableName: 'sf_product2', Id: partId, Name: partName },
                                                    ]}
                                                />
                                            </article>
                                            <article className='grid grid-cols-2'>
                                                <SFDynamicForm
                                                    formData={lineFormData}
                                                    setFormData={setLineFormData}
                                                    cols={2}
                                                    notSerialized={true}
                                                />
                                            </article>
                                        </section>
                                    </section>
                                </section>
                                <section className='w-full h-fit flex flex-row justify-evenly gap-x-5'>
                                    <button
                                        className='bg-gray-300 text-black rounded-md px-2 py-1 w-full disabled:opacity-40'
                                        onClick={() => closeModal()}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className='bg-blue-primary text-white rounded-md px-2 py-1 w-full disabled:opacity-40'
                                        onClick={() => submitRFQ()}
                                        disabled={isNotFilledOut || submitting}
                                    >
                                        {submitting ? 'Submitting...' : 'Submit'}
                                    </button>
                                </section>
                            </>
                        )}
                    </section>
                </section>
            </Dialog>
        </Transition.Root>
    );
}
